import React, { useCallback, useContext, useState, useEffect, useMemo, useRef } from 'react';

import * as R from 'ramda';
import { useHistory, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns/fp';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  CircularProgress,
  Typography,
  Button,
  TextField,
  Divider,
  InputLabel,
  Card,
  CardContent,
  Box,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { useTranslation } from 'react-i18next';
import { useForm, useFieldArray } from 'react-hook-form';
import { AppMachineContext } from '@store/appMachine';
import { Signer } from '@views/cases/components/Signer';
import { getFileName } from '@utils/data-utils';
import { emailRegex } from '@utils/regex';
import { getIdentityProviders } from '@store/authAPI';
import { changeCase, fetchCases } from '@store/appAPI';
import { DropDownSelect } from '@components/DropDownSelect';
import { FileSelector } from '@components/FileSelector';
import { DomainWarningMessage } from '@utils/userDomain';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  formatMailOptions,
  processSignRequestData,
  hasRepeatedEmail,
  checkSignerHasIdp,
} from '@utils/signRequestFormFunctions';
import { Reviewer } from '@views/cases/components/Reviewer';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  box: { maxWidth: 640, marginBottom: theme.spacing(3) },
  subtitle: {
    padding: theme.spacing(2, 0, 1, 0),
  },
  uppercase: {
    textTransform: 'uppercase',
  },
  select: {
    marginTop: '1.5em',
  },
  personalIdCheckbox: {
    marginBottom: '0',
  },
  signaturePage: {
    marginTop: '1rem',
  },
  fullWidth: {
    width: '100%',
  },
}));

const IDLE = Symbol('idle state');
const LOADING = Symbol('loading state');
const ERROR = Symbol('error state');

const isCheckboxDisabledForSigner = (signer) => signer?.signed;

export const ChangeCasePage = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation(['translations', 'errors']);
  const adminRef = useRef();
  const [signCase, setSignCase] = useState();
  const { templates, features } = useTheme();

  const reviewerOption =
    features && 'reviewerInCases' in features ? features.reviewerInCases : null;
  const [
    {
      context: { signIdps },
      matches,
    },
    send,
  ] = useContext(AppMachineContext);

  const getInitialState = () => ({
    signers: [
      ...(signCase?.Signers?.map((signer, idx) => ({
        id: `existingSigner${idx}`,
        signerId: signer.ID,
        email: signer.Email[0],
        name: signer.Name,
        personalID: signer.PersonalID,
        personalNumberCheckbox: !!signer.PersonalID, // Must be true or false, can not be undefined
        idps: signIdps.map((idp) => ({
          ...idp,
          deselected: !signer?.IdentityProviders?.some(
            (signerIdp) => idp.displayName === signerIdp.displayName,
          ),
        })),
        // State for idp checkbox(true or false)
        idpsForSigner: signIdps.reduce(
          (idpMap, idp) => ({
            ...idpMap,
            [idp.id]: !!signer?.IdentityProviders?.some(
              (signerIdp) => idp.displayName === signerIdp.displayName,
            ),
          }),
          {},
        ),
        signed: signer.Signed,
        signerHasSigned: signer.Signed,
      })) ?? []),
    ],
    reviewers: [
      ...(signCase?.Reviewers?.map((reviewer, idx) => ({
        id: `existingReviewer${idx}`,
        reviewerId: reviewer.ID,
        email: reviewer.Email,
      })) ?? []),
    ],
    signInOrder: signCase?.ForceSigningOrder,
    lang: signCase?.Language,
  });
  const authorEmail = signCase?.Author?.Email;

  const classes = useStyles();
  const history = useHistory();

  const initialized = matches('authenticated.idle') && !R.isEmpty(signIdps);
  const [formState, setFormState] = useState({ state: IDLE }); // Perhaps better to go through machine?

  const {
    control,
    register,
    reset,
    handleSubmit,
    setValue,
    setError,
    errors,
    clearErrors,
    getValues,
    watch,
  } = useForm({ defaultValues: useMemo(getInitialState, [signCase]) });
  const { fields: signers, append: addSigner, move, remove } = useFieldArray({
    control,
    name: 'signers',
    shouldUnregister: true,
  });

  const {
    fields: reviewers,
    append: appendReviewer,
    move: moveReviewer,
    remove: removeReviewer,
  } = useFieldArray({
    control,
    name: 'reviewers',
    shouldUnregister: true,
  });

  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [expirationDate, setExpirationDate] = useState();
  const [changeAdministratorsEmail, setChangeAdministratorsEmail] = useState('');
  const [mailTemplates] = useState(formatMailOptions(templates, i18n.language));
  const [repeatedEmailErrors, setRepeatedEmailErrors] = useState({});
  const [repeatedReviewerEmailErrors, setRepeatedReviewerEmailErrors] = useState([]);
  const [maxDate, setMaxDate] = useState(new Date());

  const [dateErrorMessage, setDateErrorMessage] = useState('');
  const dateInputRef = useRef(null);

  // to make useEffect run when we change something in signers
  const [signersUpdated, setSignersUpdated] = useState(false);
  const updateSignersToggle = () => setSignersUpdated(!signersUpdated);

  const [reviewersUpdated, setReviewersUpdated] = useState(false);
  const updateReviewersToggle = () => setReviewersUpdated(!reviewersUpdated);

  const isExpirationDateValid = useCallback(() => dateErrorMessage === '', [dateErrorMessage]);

  const handleDatePickerError = (errorType) => {
    const getErrorMessage = () => {
      switch (errorType) {
        case 'minDate':
          return t('The expiration date must be from today');
        case 'maxDate':
          return t('Expiration date can not be later than', {
            maxDate: maxDate.toISOString().split('T')[0],
          });
        case 'invalidDate':
          return t('invalidDate');
        default:
          return '';
      }
    };
    setDateErrorMessage(getErrorMessage());
  };

  const language = watch('lang');

  const showDomainWarning = (email) => email && email.match(emailRegex);

  useEffect(async () => {
    void getIdentityProviders().then((data) => send('SET_IDPS', { data }));
    const allCases = await fetchCases();
    const newSignCase = allCases?.find((c) => c.ID.toString() === id.toString());
    // If no sign case or everyone has signed, shouldn't change this case
    // Instead go back to the cases page
    if (!newSignCase || newSignCase?.Signers?.every((signer) => signer.Signed)) {
      history.push('/');
    }
    setSignCase(newSignCase);
  }, [send]);

  useEffect(() => {
    setFiles(signCase?.DocumentURLs?.map((path) => ({ name: getFileName(path) })));
    if (signCase && signCase.ExpirationDate && signCase?.ExpirationDate.getFullYear() !== 1) {
      setExpirationDate(format('yyyy-MM-dd', signCase.ExpirationDate));
    }
    setAttachments(signCase?.AttachmentURLs?.map((path) => ({ name: getFileName(path) })));
    const createdAt = signCase?.CreatedAt;
    const d = createdAt ? new Date(createdAt) : new Date();
    d.setDate(d.getDate() + 59);
    setMaxDate(d);
  }, [signCase]);
  useEffect(() => {
    reset(getInitialState());
  }, [reset, signCase]);

  const signerHasIdp = useCallback(
    ({ name, signerState }) => checkSignerHasIdp({ t, signIdps, setError, name, signerState }),
    [t, signIdps, setError],
  );
  const processData = useCallback(
    (data) =>
      processSignRequestData({
        signIdps,
        originalSignersData: signCase?.Signers,
        data,
        setError,
        t,
      }),
    [t, signCase, signIdps, signerHasIdp],
  );

  useEffect(() => {
    hasRepeatedEmail(
      getValues().signers,
      getValues().reviewers,
      setRepeatedEmailErrors,
      setRepeatedReviewerEmailErrors,
      t,
    );
  }, [signersUpdated, reviewersUpdated]);

  const submit = async (data) => {
    setFormState({ state: LOADING });
    const dataToSend = await processData(data);
    if (!isExpirationDateValid()) {
      setFormState({ state: ERROR });
      // ensure the DatePicker input receives focus when submit fails
      requestAnimationFrame(() => {
        if (dateInputRef.current && typeof dateInputRef.current.focus === 'function') {
          dateInputRef.current.focus();
        }
      });
      return;
    }
    if (
      hasRepeatedEmail(
        data.signers,
        data.reviewers,
        setRepeatedEmailErrors,
        setRepeatedReviewerEmailErrors,
        t,
      )
    ) {
      setFormState({ state: ERROR });
      window.scrollTo(0, 0);
      return;
    }
    if (!dataToSend) {
      setFormState({ state: ERROR });
      return;
    }
    if (dataToSend.signpage === 'No signing page') dataToSend.signpage = '';
    dataToSend.caseId = id;
    if (changeAdministratorsEmail) dataToSend.newAdmin = changeAdministratorsEmail;

    changeCase(dataToSend)
      .then(() => history.push('/'))
      .catch((err) => {
        setFormState({ state: ERROR, message: err.message });
      });
  };

  const onSubmit = (...args) => {
    clearErrors();
    handleSubmit(submit)(...args);
  };

  const disableArrows = (signerIndex) => {
    const up = signerIndex === 0 || signers[signerIndex - 1].signed || signers[signerIndex].signed;
    const down =
      signerIndex === signers.length - 1 ||
      signers[signerIndex + 1].signed ||
      signers[signerIndex].signed;
    return {
      up,
      down,
    };
  };

  if (!signCase)
    return (
      <Box p={2}>
        <CircularProgress />
      </Box>
    );

  return (
    <form className={classes.root} onSubmit={onSubmit} noValidate>
      <Card className={classes.box}>
        <CardContent>
          <FileSelector
            isChangeCaseComponent
            title={t('Uploaded documents')}
            className={classes.box}
            register={register}
            files={files}
            setValue={setValue}
            error={errors.files}
          />
        </CardContent>
      </Card>
      <Card className={classes.box}>
        <CardContent>
          <FileSelector
            isChangeCaseComponent
            title={t('Uploaded attachments')}
            className={classes.box}
            register={register}
            files={attachments}
            setValue={setValue}
            error={errors.files}
          />
        </CardContent>
      </Card>

      {signers.map((signer, index) => (
        <>
          <Signer
            name={`signers[${index}]`}
            existingEmail={signer?.email}
            existingSignerName={signer?.name}
            existingSignerPersonalID={signer?.personalID}
            disabledCheckbox={isCheckboxDisabledForSigner(signer)}
            errors={errors?.signers?.[index]}
            key={`signer${signer.id}`}
            index={index}
            classes={classes}
            totalCount={signers.length}
            setValue={(n, val) => {
              updateSignersToggle();
              setValue(n, val);
            }}
            getValues={getValues}
            move={(indexA, indexB) => {
              updateSignersToggle();
              move(indexA, indexB);
            }}
            remove={(idx) => {
              updateSignersToggle();
              remove(idx);
            }}
            register={register}
            idps={signer?.idps ?? signIdps}
            signed={signer?.signed}
            repeatedEmailErrors={repeatedEmailErrors[index]}
            disableArrows={disableArrows(index)}
            personalNumberCheckbox={signer?.personalNumberCheckbox}
            control={control}
            idpsForSigner={signer?.idpsForSigner}
          />
          <input
            name={`signers[${index}].id`}
            id="signerId"
            type="hidden"
            value={signer?.signerId ?? ''}
            ref={register()}
          />
        </>
      ))}
      <div className={classes.box}>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="secondary"
          onClick={() => {
            updateSignersToggle();
            addSigner({ email: '' });
          }}
        >
          {t('Add signer')}
        </Button>
      </div>
      {signers.length > 1 && (
        <div className={classes.box}>
          <FormControlLabel
            control={
              <Checkbox
                disabled
                size="medium"
                checked={signCase?.ForceSigningOrder}
                name="signInOrder"
                inputRef={register}
              />
            }
            label={t('Sign in order')}
          />
        </div>
      )}
      {reviewerOption && (
        <>
          {reviewers.length > 0 && (
            <>
              {reviewers.map((reviewer, i) => (
                <>
                  <Reviewer
                    key={`reviewer${reviewer.id}`}
                    name={`reviewers.${i}`}
                    existingEmail={reviewer?.email}
                    setValue={(n, val) => {
                      updateReviewersToggle();
                      setValue(n, val);
                    }}
                    getValues={getValues}
                    index={i}
                    classes={classes}
                    move={moveReviewer}
                    remove={removeReviewer}
                    totalCount={reviewers.length}
                    errors={errors?.reviewers?.[i] || {}}
                    repeatedEmailErrors={repeatedReviewerEmailErrors[i]}
                    control={control}
                  />
                  <input
                    name={`reviewers[${i}].id`}
                    id="reviewerId"
                    type="hidden"
                    value={reviewer?.reviewerId ?? ''}
                    ref={register()}
                  />
                </>
              ))}
            </>
          )}
          <div className={classes.box}>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              color="secondary"
              onClick={() => {
                appendReviewer({ email: '' });
                updateReviewersToggle();
              }}
            >
              {t('Add reviewer')}
            </Button>
          </div>
        </>
      )}
      <Card className={classes.box}>
        <CardContent>
          <div className={classes.box}>
            <Typography variant="subtitle1" className={classes.uppercase}>
              {t('Message to signers')}
            </Typography>
            <Divider />
            <DropDownSelect
              items={mailTemplates}
              register={register}
              refName="lang"
              label={t('language')}
              setValue={setValue}
              defaultValue={language}
            />
            <InputLabel htmlFor="message" className={classes.subtitle}>
              {t('Message (optional)')}
            </InputLabel>
            <TextField
              id="message"
              name="message"
              inputRef={register}
              fullWidth
              multiline
              rows="8"
              inputProps={{
                maxLength: 2048,
              }}
            />
          </div>
          <div className={classes.box}>
            <Typography variant="subtitle1" className={classes.uppercase}>
              {t('Expiration date')}
            </Typography>
            <Divider />
            <InputLabel htmlFor="date" className={classes.subtitle}>
              {t('Choose date (optional)')}
            </InputLabel>
            <DatePicker
              name="date"
              format="yyyy-MM-dd"
              inputRef={register}
              value={expirationDate ? parseISO(expirationDate) : null}
              InputLabelProps={{ shrink: true }}
              minDate={parseISO(format('yyyy-MM-dd', new Date()))}
              maxDate={parseISO(format('yyyy-MM-dd', maxDate))}
              className={classes.fullWidth}
              onError={handleDatePickerError}
              onAccept={() => setDateErrorMessage('')}
              slotProps={{
                openPickerButton: { 'aria-label': t('Toggle date picker') },
                textField: {
                  id: 'date',
                  name: 'date',
                  clearable: true,
                  inputRef: (el) => {
                    dateInputRef.current = el;
                    register(el);
                  },
                  inputProps: {
                    'aria-describedby': dateErrorMessage ? 'date-error' : undefined,
                  },
                },
              }}
              onChange={(date) => {
                setExpirationDate(date ? format('yyyy-MM-dd', date.getTime()) : null);
              }}
            />
            {dateErrorMessage && (
              <Typography id="date-error" color="error" role="alert" aria-live="assertive">
                {dateErrorMessage}
              </Typography>
            )}
          </div>
        </CardContent>
      </Card>
      <Card className={classes?.box}>
        <CardContent>
          <Typography variant="subtitle1" className={classes?.uppercase}>
            {t('Administrator')}
          </Typography>
          <Divider />
          <InputLabel htmlFor="email" className={classes?.subtitle} required>
            {t('Email')}
          </InputLabel>
          <TextField
            multiline
            rows={1}
            rowsMax={1}
            margin="dense"
            id="email"
            name="administratorEmail"
            fullWidth
            required
            defaultValue={authorEmail}
            error={!!errors?.administratorEmail}
            inputRef={(el) => {
              adminRef.current = el;
              if (el) {
                const reg = register({
                  required: t('This field is required'),
                  pattern: { value: emailRegex, message: t('Invalid email') },
                });
                reg(el);
              }
            }}
            inputProps={{
              maxLength: 254,
              'aria-required': 'true',
              'aria-describedby': errors?.administratorEmail ? 'admin-error' : undefined,
            }}
            onChange={(e) => {
              // keep your local state for the domain warning, no custom validation here
              const newEmail = String(e.target.value).toLowerCase();
              setChangeAdministratorsEmail(newEmail);
            }}
          />
          {errors?.administratorEmail && (
            <Typography id="admin-error" color="error" role="alert" aria-live="assertive">
              {errors.administratorEmail.message}
            </Typography>
          )}
          {showDomainWarning(changeAdministratorsEmail) && (
            <DomainWarningMessage email={changeAdministratorsEmail} />
          )}
        </CardContent>
      </Card>
      <div className={classes.box}>
        {formState.state === ERROR && formState.message && (
          <p>
            <Typography color="error">{t(formState.message, { ns: 'errors' })}</Typography>
          </p>
        )}
        {formState.state === LOADING || !initialized ? (
          <CircularProgress />
        ) : (
          <Button type="submit" variant="contained" color="primary">
            {t('Save and send')}
          </Button>
        )}
      </div>
    </form>
  );
};
